/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { featuresSet } from "../funcs/featuresSet.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetProductFeatureRequest,
  SetProductFeatureSecurity,
} from "../models/operations/setproductfeature.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type FeaturesSetMutationVariables = {
  request: SetProductFeatureRequest;
  security?: SetProductFeatureSecurity | undefined;
  options?: RequestOptions;
};

export type FeaturesSetMutationData = void;

export type FeaturesSetMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setProductFeature features
 *
 * @remarks
 * Enable or disable an organization feature flag. Staff-managed entitlements (such as sso and scim) additionally require a Speakeasy platform administrator; organization admins can set only the org-settable operational toggles.
 */
export function useFeaturesSetMutation(
  options?: MutationHookOptions<
    FeaturesSetMutationData,
    FeaturesSetMutationError,
    FeaturesSetMutationVariables
  >,
): UseMutationResult<
  FeaturesSetMutationData,
  FeaturesSetMutationError,
  FeaturesSetMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildFeaturesSetMutation(client, options),
    ...options,
  });
}

export function mutationKeyFeaturesSet(): MutationKey {
  return ["@gram/client", "features", "set"];
}

export function buildFeaturesSetMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: FeaturesSetMutationVariables,
  ) => Promise<FeaturesSetMutationData>;
} {
  return {
    mutationKey: mutationKeyFeaturesSet(),
    mutationFn: function featuresSetMutationFn({
      request,
      security,
      options,
    }): Promise<FeaturesSetMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(featuresSet(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
